<template>
  <OnBoardingFrame
    :step="2"
    :title="$t('on_boarding.profile_photo_page.title')"
    :subtitle="$t('on_boarding.profile_photo_page.subtitle')"
    :error="errorMessage"
    split-footer
  >
    <!-- Step 2. Mirrors the mobile photo step (Android's OnboardingSelectPhotoFragment,
         iOS's OnBoardingSelectProfilePictureViewController): a grid of the HiiKER
         avatars plus the option to upload your own. The picture is optional — Skip
         for now sits next to Continue, which waits for a picture.

         No Back here. This is the first step after the account is created, and
         everything behind it on the stack is sign-in: the email form would be
         resubmitted for an account that now exists (auth/email-already-in-use),
         and the auth buttons are meaningless to someone already signed in. -->
    <ul class="ob-avatars presets" role="listbox" :aria-label="$t('on_boarding.profile_photo_page.presets_label')">
      <li>
        <!-- First cell, matching the mobile layout. A label wrapping the input
             keeps it operable by click and by keyboard without a second control. -->
        <label
          class="ob-avatar ob-avatar--add preset preset--add"
          :class="{ 'preset--selected': !!customPreview }"
          :title="$t('on_boarding.profile_photo_page.upload_your_own')"
        >
          <input
            type="file"
            accept="image/*"
            class="ob-avatar__input"
            :aria-label="$t('on_boarding.profile_photo_page.upload_your_own')"
            @change="onFile"
          >
          <img v-if="customPreview" :src="customPreview" alt="">
          <!-- Icon only: a label never fits a circle this size in every
               language. The input's aria-label and the title name it. -->
          <span v-else class="ms" aria-hidden="true">add_a_photo</span>
        </label>
      </li>
      <li v-for="avatar in presets" :key="avatar.id">
        <button
          type="button"
          class="ob-avatar preset"
          :class="{ 'preset--selected': avatar.id === selectedPresetId }"
          role="option"
          :aria-selected="avatar.id === selectedPresetId"
          :title="labelFor(avatar.id)"
          @click="choose(avatar)"
        >
          <img :src="avatar.src" :alt="labelFor(avatar.id)" loading="lazy">
        </button>
      </li>
    </ul>

    <template #footer>
      <button type="button" class="ob-btn ob-btn-secondary ob-skip" @click="skip">
        {{ $t('on_boarding.profile_photo_page.skip') }}
      </button>
      <button type="button" class="ob-btn ob-btn-primary" :disabled="!preview" @click="submit">
        {{ $t('on_boarding.profile_photo_page.continue') }}
      </button>
    </template>
  </OnBoardingFrame>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import OnBoardingFrame from './OnBoardingFrame.vue';
import { PRESET_AVATARS, avatarLabel, type PresetAvatar } from '../../../utils/helpers/presetAvatars';

export default defineComponent({
  name: 'OnBoardingProfilePhotoPage',
  components: { OnBoardingFrame },
  data() {
    return { presets: PRESET_AVATARS as PresetAvatar[] };
  },
  computed: {
    errorMessage(): string | null {
      return this.$store.state.onBoarding.errorMessage;
    },
    preview(): string | null {
      return this.$store.state.onBoarding.profileAvatarPreview;
    },
    selectedPresetId(): string | null {
      return this.$store.state.onBoarding.profileAvatarPresetId;
    },
    // Only an uploaded photo needs its own thumbnail; a preset is already
    // shown as selected in the grid.
    customPreview(): string | null {
      const state = this.$store.state.onBoarding;
      return state.profileAvatarPresetId ? null : state.profileAvatarPreview;
    },
  },
  methods: {
    labelFor(id: string): string {
      return avatarLabel(id);
    },
    choose(avatar: PresetAvatar) {
      this.$store.dispatch('onBoarding/selectPresetAvatar', avatar);
    },
    onFile(event: Event) {
      const input = event.target as HTMLInputElement;
      const file = input.files && input.files[0];
      if (file) this.$store.dispatch('onBoarding/selectCustomAvatar', file);
      // Clear the input so picking the same file twice still fires a change.
      input.value = '';
    },
    submit() {
      this.$store.dispatch('onBoarding/submitProfilePhoto');
    },
    // Moves on without a picture, dropping any already picked.
    skip() {
      this.$store.dispatch('onBoarding/skipProfilePhoto');
    },
  },
  beforeUnmount() {
    this.$store.commit('onBoarding/setError', null);
  },
});
</script>
